import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Testimonials = () => {
  const featuredTestimonial = {
    quote: "Automatix's Fusion Of AI And Innovation Set Our Project Apart. Their Solutions Are Second To None.",
    author: "Zidane Muharto",
    position: "Chief Technology Officer",
    company: "Zapfast",
    logo: "https://framerusercontent.com/images/uBxSkZWWS6tVxAX4UJt1VLLMk.png?scale-down-to=512",
    image: "https://framerusercontent.com/images/SGKTtzQfO2LV9AOvAEQlg1H0vw.jpg?scale-down-to=512",
    metrics: [
      { value: "73%", description: "Sales increase in first month" },
      { value: "5X", description: "Faster customer resolutions" }
    ]
  };

  const testimonials = [
    {
      quote: "The creativity and AI expertise from Automatix set a new benchmark for our industry. Highly recommended!",
      author: "Agus Blimbing",
      position: "Tech Manager",
      company: "CreativEdge",
      stars: 5
    },
    {
      quote: "Automatix's revolutionary AI approach and creative solutions elevated our project. Stellar performance!",
      author: "Steve Kebalen",
      position: "AI Developer",
      company: "BrightNest",
      stars: 5
    },
    {
      quote: "The blend of AI and creativity at Automatix transformed our vision into reality. Exceptional support!",
      author: "John Kepanjen",
      position: "E-Commerce Stacks",
      company: "PrimeCore",
      stars: 5
    }
  ];

  return (
    <Section>
      <ContentWrapper>
        <SectionTag>
        <TestimonialsPill>
          What Our Users Say
        </TestimonialsPill>
        </SectionTag>
        <MainHeading>
          Trusted By Businesses Like Yours
        </MainHeading>
      </ContentWrapper>

      <TestimonialsContainer>
        <FeaturedTestimonial>
          <FeaturedImage>
            <PersonImage src={featuredTestimonial.image} alt={featuredTestimonial.author} />
          </FeaturedImage>

          <TestimonialContent>
            <CompanyLogo>
              <img src={featuredTestimonial.logo} alt={featuredTestimonial.company} />
              <span>{featuredTestimonial.company}</span>
            </CompanyLogo>

            <Quote>"{featuredTestimonial.quote}"</Quote>

            <AuthorInfo>
              <Author>{featuredTestimonial.author}</Author>
              <Position>{featuredTestimonial.position}</Position>
            </AuthorInfo>

            <MetricsContainer>
              {featuredTestimonial.metrics.map((metric, index) => (
                <Metric key={index}>
                  <MetricValue>{metric.value}</MetricValue>
                  <MetricDescription>{metric.description}</MetricDescription>
                </Metric>
              ))}
            </MetricsContainer>
          </TestimonialContent>
        </FeaturedTestimonial>

        <TestimonialGrid>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <StarRating>
                {[...Array(testimonial.stars)].map((_, i) => (
                  <Star key={i}>★</Star>
                ))}
              </StarRating>

              <TestimonialQuote>"{testimonial.quote}"</TestimonialQuote>

              <TestimonialAuthor>
                <AuthorName>{testimonial.author}</AuthorName>
                <AuthorPosition>{testimonial.position}</AuthorPosition>
              </TestimonialAuthor>

              <CompanyName>{testimonial.company}</CompanyName>
            </TestimonialCard>
          ))}
        </TestimonialGrid>
      </TestimonialsContainer>
    </Section>
  );
};

const Section = styled.section`
  padding: 100px 20px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const SectionTag = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 14px;
  padding: 8px 20px;
  border-radius: 50px;
  display: inline-block;
  margin-bottom: 30px;
`;

const MainHeading = styled.h2`
  font-size: 38px;
  font-weight: 700;
  color: white;
  margin-bottom: 60px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const TestimonialsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
`;

const FeaturedTestimonial = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FeaturedImage = styled.div`
  position: relative;
  height: 500px;
  border-radius: 20px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 400px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const PersonImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TestimonialContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  img {
    height: 30px;
    width: auto;
  }

  span {
    font-size: 18px;
    font-weight: 600;
    color: white;
  }
`;

const Quote = styled.blockquote`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.4;
  color: white;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const AuthorInfo = styled.div`
  margin-bottom: 40px;
`;

const Author = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin-bottom: 5px;
`;

const Position = styled.div`
  font-size: 16px;
  color: var(--color-text-gray);
`;

const MetricsContainer = styled.div`
  display: flex;
  gap: 40px;
`;

const Metric = styled.div``;

const MetricValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: var(--color-ui-orange);
  margin-bottom: 5px;
`;

const MetricDescription = styled.div`
  font-size: 14px;
  color: var(--color-text-gray);
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background-color: #111;
  border-radius: 20px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StarRating = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 20px;
`;

const Star = styled.span`
  color: var(--color-ui-orange);
  font-size: 18px;
`;

const TestimonialQuote = styled.blockquote`
  font-size: 16px;
  line-height: 1.6;
  color: white;
  flex-grow: 1;
  margin-bottom: 30px;
`;

const TestimonialAuthor = styled.div`
  margin-bottom: 10px;
`;

const AuthorName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 5px;
`;

const AuthorPosition = styled.div`
  font-size: 14px;
  color: var(--color-text-gray);
`;

const CompanyName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-light);
`;

const TestimonialsPill = styled.div`
  display: inline-block;
  padding: 8px 20px;
  background-color: rgba(17, 17, 17, 0.9);
  border-radius: 50px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export default Testimonials;
